import { assertSafe, ValidationError } from "./schema.js";
import { getDefinition } from "./registry.js";
import { dimensionForUnit } from "./units.js";

export const qualities = ["good", "stale", "missing", "disconnected"];
/** Normalize transport JSON. No values or measurement quality are invented. */
export function normalizeData(instance, input = {}) {
  assertSafe(input);
  const result = structuredClone(input),
    definition = getDefinition(instance.definitionId);
  const issues = [];
  for (const [role, r] of Object.entries(result.values ?? {})) {
    if (!r || typeof r !== "object" || Array.isArray(r) || !("value" in r)) {
      issues.push({
        path: `values.${role}`,
        message: "A reading object with value is required",
      });
      continue;
    }
    if (!qualities.includes(r.quality))
      issues.push({
        path: `values.${role}.quality`,
        message: "Explicit authoritative quality required",
      });
    const expected = definition.roles[role];
    if (
      expected &&
      expected.dimension !== "any" &&
      r.unit &&
      dimensionForUnit(r.unit) &&
      dimensionForUnit(r.unit) !== expected.dimension
    )
      issues.push({
        path: `values.${role}.unit`,
        message: "Incompatible role dimension",
      });
    if (
      expected?.references.length &&
      r.reference &&
      !expected.references.includes(r.reference)
    )
      issues.push({
        path: `values.${role}.reference`,
        message: "Incompatible role reference",
      });
    const binding = instance.bindings[role];
    if (binding?.reference && r.reference !== binding.reference)
      issues.push({
        path: `values.${role}.reference`,
        message: "Reported reference does not match the binding",
      });
    if (
      r.measuredAt !== undefined &&
      (!Number.isFinite(r.measuredAt) || Math.abs(r.measuredAt) > 8.64e15)
    )
      issues.push({
        path: `values.${role}.measuredAt`,
        message: "Invalid timestamp",
      });
  }
  for (const [role, series] of Object.entries(result.history ?? {})) {
    if (!Array.isArray(series?.points) || series.points.length > 10000) {
      issues.push({
        path: `history.${role}`,
        message: "At most 10000 ordered points required",
      });
      continue;
    }
    let last = -Infinity;
    for (const p of series.points) {
      if (
        !Number.isFinite(p.time) ||
        Math.abs(p.time) > 8.64e15 ||
        p.time < last ||
        !qualities.includes(p.quality) ||
        !("value" in p)
      )
        issues.push({
          path: `history.${role}`,
          message:
            "Ordered finite timestamps, values and explicit quality required",
        });
      last = p.time;
    }
  }
  if (result.quality !== undefined && !qualities.includes(result.quality))
    issues.push({ path: "quality", message: "Unknown quality" });
  if (
    result.now !== undefined &&
    (!Number.isFinite(result.now) || Math.abs(result.now) > 8.64e15)
  )
    issues.push({ path: "now", message: "Invalid timestamp" });
  if (
    result.media &&
    (!["image", "video", "web", "asset"].includes(result.media.kind) ||
      typeof result.media.approved !== "boolean")
  )
    issues.push({
      path: "media",
      message: "Host-approved media descriptor required",
    });
  if (issues.length) throw new ValidationError(issues);
  return result;
}

/** Aggregate measured buckets without filling gaps; directional quantities use circular mean. */
export function aggregateHistory(points, bucketMs, method = "mean") {
  if (
    !Number.isFinite(bucketMs) ||
    bucketMs <= 0 ||
    !["mean", "min", "max", "last", "circular"].includes(method)
  )
    throw new Error("Valid bucket and aggregation required");
  const buckets = new Map();
  for (const p of points) {
    const key = Math.floor(p.time / bucketMs) * bucketMs;
    const list = buckets.get(key) ?? [];
    list.push(p);
    buckets.set(key, list);
  }
  return [...buckets]
    .sort(([a], [b]) => a - b)
    .map(([time, list]) => {
      if (list.some((p) => p.quality !== "good" || typeof p.value !== "number"))
        return { time, value: null, quality: "missing" };
      const values = list.map((p) => p.value);
      let value;
      if (method === "circular") {
        const x = values.reduce((s, v) => s + Math.cos((v * Math.PI) / 180), 0),
          y = values.reduce((s, v) => s + Math.sin((v * Math.PI) / 180), 0);
        value =
          Math.hypot(x, y) < 1e-10
            ? null
            : ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
      } else
        value =
          method === "last"
            ? values.at(-1)
            : method === "min"
              ? Math.min(...values)
              : method === "max"
                ? Math.max(...values)
                : values.reduce((a, b) => a + b, 0) / values.length;
      return { time, value, quality: value === null ? "missing" : "good" };
    });
}
